/**
 * The agency's own overview: what the whole portfolio adds up to, which
 * creators need somebody today, and what has happened lately. Every figure is
 * counted by the API from the canonical rows; nothing here is a snapshot.
 */

import { AuthorizationStatus, ScanRunStatus } from './enums';
import type { CustomerCaseStatus } from './customer-case-status';

/**
 * Where one creator's protection stands. Exactly one per creator, so the four
 * add up to the creator count.
 *
 * - `setup`: no accepted authorization letter, or never searched for.
 * - `first_search`: set up, and the first search has not finished yet.
 * - `attention`: set up and searched, but the latest search failed.
 * - `active`: set up, and the latest search finished.
 */
export type ProtectionState = 'active' | 'first_search' | 'attention' | 'setup';

export const PROTECTION_STATES: readonly ProtectionState[] = ['active', 'first_search', 'attention', 'setup'];

export const PROTECTION_LABEL: Readonly<Record<ProtectionState, string>> = {
  active: 'Searching on schedule',
  first_search: 'First search running',
  attention: 'Last search failed',
  setup: 'Setup incomplete',
};

/** What decides a creator's protection state. */
export interface ProtectionFacts {
  authorizationStatus: AuthorizationStatus;
  /** When her first search was queued; null means nobody has started one. */
  firstScanQueuedAt: string | Date | null;
  /** How her most recent search ended, or null when there has been none. */
  latestScanStatus: ScanRunStatus | null;
  /** When any search of hers last finished. */
  lastScanAt: string | Date | null;
  /**
   * Paid agency only: invite + OF bio + letter. Omit or true when the gate
   * does not apply (solo, or the unpaid free-scan path).
   */
  agencySetupComplete?: boolean;
}

export function protectionStateOf(facts: ProtectionFacts): ProtectionState {
  if (facts.authorizationStatus !== AuthorizationStatus.VERIFIED || !facts.firstScanQueuedAt) return 'setup';
  // Paid agency checklist (invite + bio) after the letter: undefined means
  // the caller is not applying that gate (solo, or unpaid free-scan path).
  if (facts.agencySetupComplete === false) return 'setup';
  // A failure is shown before "still running": a creator whose only search
  // failed is not waiting, she is stuck.
  if (facts.latestScanStatus === ScanRunStatus.FAILED) return 'attention';
  if (!facts.lastScanAt || facts.latestScanStatus === null) return 'first_search';
  return 'active';
}

/**
 * Why a creator needs somebody, highest priority first. The order is the
 * product's own: nothing can be filed without the letter, a failed search
 * means nobody is looking, and findings not yet in a removal wait on a person.
 */
export type CreatorAttention =
  | 'invite_missing'
  | 'bio_missing'
  | 'letter_rejected'
  | 'letter_missing'
  | 'scan_failed'
  | 'unfiled_detections';

export const ATTENTION_ORDER: readonly CreatorAttention[] = [
  'invite_missing',
  'bio_missing',
  'letter_rejected',
  'letter_missing',
  'scan_failed',
  'unfiled_detections',
];

/** The facts a creator's attention reasons are read from. */
export interface AttentionFacts {
  authorizationStatus: AuthorizationStatus;
  latestScanStatus: ScanRunStatus | null;
  /** Active detections not yet put into any removal. */
  unfiledDetections: number;
  /** Paid agency: invitation into her own cabinet is not confirmed yet. */
  inviteMissing?: boolean;
  /** Paid agency: OnlyFans bio notice not verified (or later lost). */
  bioMissing?: boolean;
}

/**
 * Only what the agency can act on: a letter with support (`uploaded`) is
 * waiting on us, so it shows as setup incomplete and is not listed here.
 */
export function attentionReasonsOf(facts: AttentionFacts): CreatorAttention[] {
  const reasons = new Set<CreatorAttention>();
  if (facts.inviteMissing) reasons.add('invite_missing');
  if (facts.bioMissing) reasons.add('bio_missing');
  if (facts.authorizationStatus === AuthorizationStatus.REJECTED) reasons.add('letter_rejected');
  if (facts.authorizationStatus === AuthorizationStatus.PENDING) reasons.add('letter_missing');
  if (facts.latestScanStatus === ScanRunStatus.FAILED) reasons.add('scan_failed');
  if (facts.unfiledDetections > 0) reasons.add('unfiled_detections');
  return ATTENTION_ORDER.filter((reason) => reasons.has(reason));
}

/** One creator in the agency overview's lists. Never her legal name or addresses. */
export interface OverviewCreator {
  id: string;
  stageName: string;
  /** Her first handle, without the @; null when she has none. */
  username: string | null;
  state: ProtectionState;
  activeDetections: number;
  unfiledDetections: number;
  openRemovals: number;
  lastScanAt: string | null;
  latestScan: { id: string; status: ScanRunStatus; at: string } | null;
}

export interface AttentionItem {
  creator: OverviewCreator;
  /** Highest priority first; the first is the one the row leads with. */
  reasons: CreatorAttention[];
}

export type ActivityKind =
  | 'creator_added'
  | 'invite_confirmed'
  | 'bio_verified'
  | 'scan_complete'
  | 'scan_failed'
  | 'case_opened'
  | 'case_closed';

export interface ActivityItem {
  /** Unique across kinds: the same row is never two events. */
  id: string;
  kind: ActivityKind;
  at: string;
  profileId: string;
  stageName: string;
  /** Pages found by a finished search, or the site a removal is for. */
  newDetections?: number;
  domain?: string;
  caseId?: string;
  /** How a closed removal ended: done, declined or cancelled. */
  caseStatus?: string;
}

export interface AgencyOverview {
  totals: {
    creators: number;
    needsAttention: number;
    activeDetections: number;
    openRemovals: number;
    /** Creators with at least one open removal. */
    creatorsWithOpenRemovals: number;
    /** Active detections not yet in any removal. */
    unfiledDetections: number;
  };
  protection: Record<ProtectionState, number>;
  /** Creators with each reason, counted over everyone rather than the short list. */
  attentionByReason: Record<CreatorAttention, number>;
  /** Every case of these creators, by the status the client is shown. */
  removals: Record<CustomerCaseStatus, number>;
  attention: AttentionItem[];
  /** A few creators, those needing somebody first, then the most recently searched. */
  creators: OverviewCreator[];
  /** The heaviest current workload: most active detections, then most open removals. */
  workload: OverviewCreator[];
  activity: ActivityItem[];
}

export const OVERVIEW_TREND_DAYS = [7, 30, 90] as const;
export type OverviewTrendDays = (typeof OVERVIEW_TREND_DAYS)[number];

/** One UTC day's movement. The same definitions as the monthly report. */
export interface OverviewTrendDay {
  /** `YYYY-MM-DD`, UTC. */
  day: string;
  /** Pages seen for the first time (`firstSeen`). */
  found: number;
  /** Pages confirmed gone at the source (`goneAt`). */
  removed: number;
  /** Pages cleared out of search (`delistedAt`). Never added to `removed`. */
  delisted: number;
  /** Removal cases opened. */
  opened: number;
  /** Removal cases closed as done: every page gone. */
  completed: number;
}

export type OverviewTrendTotals = Omit<OverviewTrendDay, 'day'>;

export interface OverviewTrend {
  days: OverviewTrendDays;
  /** Oldest first, one entry per day, the last being today. */
  series: OverviewTrendDay[];
  totals: OverviewTrendTotals;
  /** The same window immediately before, for comparison. */
  previous: OverviewTrendTotals;
  /** When the agency's first creator was added; null with none. */
  since: string | null;
}

/** How many rows each list on the overview carries. */
export const OVERVIEW_LIST_SIZE = 6;
export const OVERVIEW_ACTIVITY_SIZE = 10;

/**
 * The attention list, ordered: by the highest reason each creator has, then by
 * how many unfiled detections, then by name. Deterministic, and no severity
 * score: the product has none, and one invented here would be believed.
 */
export function rankAttention(items: AttentionItem[]): AttentionItem[] {
  const rank = (item: AttentionItem) => ATTENTION_ORDER.indexOf(item.reasons[0]);
  return [...items].sort(
    (a, b) =>
      rank(a) - rank(b) ||
      b.creator.unfiledDetections - a.creator.unfiledDetections ||
      a.creator.stageName.localeCompare(b.creator.stageName) ||
      a.creator.id.localeCompare(b.creator.id),
  );
}
